import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router";
import { Col, Row, Container } from "@amzn/stencil-react-components/layout";
import { ExpanderContent } from "@amzn/stencil-react-components/expander";
import { TriggerButton } from "@amzn/stencil-react-components/helpers";
import { H2, Text } from "../../typography";

import {
  BADGE_PHOTO,
  CREATE_EMPLOYEE_A_TO_Z_ACCOUNT,
  EMPLOYMENT_PAPERWORK,
  I_9_DOCUMENT,
  isTaskEnabled,
  NEW_HIRE_VIDEO,
  ORDER_YOUR_SAFETY_SHOE,
  Task,
  TaskGroup,
  PRE_HIRE_APPOINTMENT_GROUP,
  FIRST_DAY_GROUP,
} from "../../../config/tasks";
import { StatusEnum, withStatus } from "../../../hoc/with-status";
import { DueDateIndicator } from "./due-date";
import { ProgressBar } from "./progress-bar";
import {
  getJobScheduleDetails,
  getNHEAppointment,
  NheDetails,
  JobScheduleDetails,
} from "../../../utility/application-data";
import { Stage } from "../../../helpers/get-settings";
import { TaskDetails } from "./task-details";
import useArb from "../../../hooks/use-arb";
import {
  ZapposPurchasePromotion,
  ZapposPurchasePromotionButton,
} from "../../zappos-purchase-promotion/ui-redisign/zappos-purchase-promotion";

import { TaskCardTextContent } from "./task-card-text-content";
import {
  I9TaskCard,
  I9TaskCardButton,
} from "../../i9-task/ui-redesign/i9-task-card";
import { BadgePhoto, BadgePhotoTaskCardButton } from "../../badge-photo";
import { NewHireVideoFlyout } from "../../new-hire-video/ui-redesign/new-hire-video";
import {
  EmploymentPaperwork,
  EmploymentPaperworkButton,
} from "../../employment-paperwork/employment-paperwork-task";
import {
  majorSectionBackgroundColor,
  newUIBackgroundColor,
} from "../../../config/palette";
import * as adobeAnalytics from "../../../utility/adobe-analytics";
import { isDragonstone } from "../../../helpers/url-params";

import { ATOZ_ENDPOINT, ZAPPOS_URL } from "../../../config/urls";
import {
  AtoZAccountCreation,
  CreateEmployeeAccountButton,
} from "../../atoz-status/employee-account-creation";
import { formatedStartTime } from "../../../utility/time-processor";

interface TaskProps {
  task: Task;
  applicationId: string;
  candidateId: string;
  bundle: any;
  onTaskStatusEvent: (k?: string, v?: StatusEnum) => void;
  stage: Stage;
  icon?: string;
  dueDate?: Date;
  shouldOverrideStatus?: Record<string, StatusEnum>;
}
export const TaskItem = ({
  task,
  applicationId,
  candidateId,
  bundle,
  icon,
  onTaskStatusEvent,
  stage,
  dueDate,
  shouldOverrideStatus,
}: TaskProps) => {
  const [businessLine, setBusinessLine] = useState("");
  const [isTaskDetailsViewOpen, setIsTaskDetailsViewOpen] = useState(false);

  const completeI9Title = bundle.getMessage(
    "Checklist-MainPage-CompleteI9-Title"
  );
  const badgePhotoTitle = bundle.getMessage(
    "Checklist-MainPage-BadgePhoto-Title"
  );
  const orderYourSafetyShoesTitle = bundle.getMessage(
    "Checklist-MainPage-MindYourStep-Title"
  );
  const startEmploymentPaperworkButton = bundle.getMessage(
    "Checklist-MainPage-Document-Status-Start-Employment-Paperwork"
  );
  const atoZSetupAZAccountButton = bundle.getMessage(
    "Checklist-AtoZ-Setup-Button"
  );
  const videoTitle = bundle.getMessage("Checklist-MainPage-Video-Title");

  const openTaskDetailsView = useCallback(() => {
    setIsTaskDetailsViewOpen(true);
    sendAdobeEvents();
  }, [isTaskDetailsViewOpen]);

  const closeTaskDetailsView = useCallback(() => {
    setIsTaskDetailsViewOpen(false);
  }, [isTaskDetailsViewOpen]);

  let enableTask = false;
  // TODO Make the enable/disable task more generic for all tasks and use app config.
  const DisabledBusinessLines = ["AIR"];

  const { requisitionId } = useParams<{
    requisitionId: string;
  }>();

  const sendAdobeEvents = useCallback(() => {
    if (task.getTaskStatusByBBId && task.analyticsOnExpand) {
      adobeAnalytics.addEventMetricWithBBId(
        window,
        applicationId,
        "click text",
        {
          text: task.analyticsOnExpand,
          jobId: requisitionId,
          isDragonstone: isDragonstone(requisitionId),
        }
      );
    } else {
      adobeAnalytics.addEventMetric(window, applicationId, "click text", {
        text: task.analyticsOnExpand,
        jobId: requisitionId,
        isDragonstone: isDragonstone(requisitionId),
      });
    }
  }, [task, applicationId, candidateId, requisitionId]);

  useEffect(() => {
    (async () => {
      const scheduleDetails = await getJobScheduleDetails(applicationId);
      setBusinessLine(scheduleDetails?.businessLine as string);
    })();
  }, []);

  const renderCard = (status?: StatusEnum, taskId?: string) => {
    function getDetailsTitle(taskName: string | undefined): string {
      switch (taskName) {
        case ORDER_YOUR_SAFETY_SHOE:
          return orderYourSafetyShoesTitle;
        case I_9_DOCUMENT:
          return completeI9Title;
        case BADGE_PHOTO:
          return badgePhotoTitle;
        case EMPLOYMENT_PAPERWORK:
          return startEmploymentPaperworkButton;
        case CREATE_EMPLOYEE_A_TO_Z_ACCOUNT:
          return atoZSetupAZAccountButton;
        case NEW_HIRE_VIDEO:
          return videoTitle;
        default:
          return taskName ?? "";
      }
    }

    // status passed in is the real status from api, override the status if needed
    const overrideStatus =
      status && shouldOverrideStatus && shouldOverrideStatus[status]
        ? shouldOverrideStatus[status]
        : status;

    const onClickAction =
      overrideStatus != StatusEnum.NOT_AVAILABLE_YET &&
      overrideStatus != StatusEnum.INELIGIBLE
        ? openTaskDetailsView
        : undefined;

    return (
      <>
        <Row display="block">
          <Col
            key={task.titleText}
            title={task.titleText}
            minWidth={window.length}
            onClick={onClickAction}
            className={"task-item"}
          >
            <Container>
              <Row>
                <Col
                  backgroundColor={renderBGColor(overrideStatus)}
                  width={"6px"}
                />
                <Col>
                  <Row>
                    <DueDateIndicator
                      status={overrideStatus}
                      dueDate={dueDate}
                    />
                  </Row>
                  <Row>
                    <Col padding="S300">
                      <img src={icon} alt="task icon" width="72px" />
                    </Col>
                    <Col padding="S300">
                      <TaskCardTextContent
                        taskTitleText={task.titleText}
                        taskName={task.taskName}
                        status={status}
                        shouldOverrideStatus={shouldOverrideStatus}
                        duration={task.duration}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </Col>
        </Row>
        <TaskDetails
          detailsTitle={getDetailsTitle(task.taskName)}
          bundle={bundle}
          buttons={getTaskDetailsButtons(
            taskId,
            task.taskName,
            status,
            shouldOverrideStatus
          )}
          flyoutContent={getTaskDetailsFlyoutContent(
            task.taskName,
            taskId,
            status,
            shouldOverrideStatus
          )}
          isTaskDetailsViewOpen={isTaskDetailsViewOpen}
          closeTaskDetailsView={closeTaskDetailsView}
          duration={task.duration}
        />
      </>
    );
  };

  if (isTaskEnabled(task.taskName as string, "TASK", stage)) {
    if (
      task.checkBusinessLine &&
      DisabledBusinessLines.includes(businessLine)
    ) {
      enableTask = false;
    } else {
      enableTask = true;
    }
  }

  if (!enableTask) {
    return null;
  }

  return withStatus({
    key: task.titleText,
    statusPath: task.titleStatusPath,
    taskStatusName: task.taskStatusName,
    getTaskStatusByBBId: task.getTaskStatusByBBId,
    applicationId,
    candidateId,
    hideWhenError: task.hideWhenError,
    isFallback: task.isFallback,
    renderComponent: renderCard,
    shouldOverrideStatus: task.shouldOverrideStatus,
    hideOnTaskStatusError: task.hideOnTaskStatusError,
    bundle,
    onTaskStatusEvent,
  });
};

interface TasksGroupProps {
  data: TaskGroup;
  applicationId: string;
  candidateId: string;
  onTaskStatusEvent: (k?: string, v?: StatusEnum) => void;
  taskStatusMap: Map<string, StatusEnum>;
  stage: Stage;
  bundle: any;
  nheDetails?: NheDetails;
  jcDetails?: JobScheduleDetails;
}
export const TasksGroup = ({
  data,
  applicationId,
  candidateId,
  onTaskStatusEvent,
  taskStatusMap,
  stage,
  bundle,
  nheDetails,
  jcDetails,
}: TasksGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  let dueDate: Date;
  let completedTasks = 0;
  let totalTasks = 0;

  data.tasks.forEach((task: Task) => {
    if (taskStatusMap.has(task.titleText)) {
      if (taskStatusMap.get(task.titleText) == StatusEnum.COMPLETED) {
        completedTasks++;
      }
      totalTasks++;
    }
  });
  if (data.id === PRE_HIRE_APPOINTMENT_GROUP && nheDetails?.nheDate) {
    dueDate = new Date(nheDetails.nheDate); // nheDetails.nheDate format: Sep 28, 2022
  } else if (data.id === FIRST_DAY_GROUP && jcDetails?.firstDayOnSiteL10N) {
    // jcDetails.firstDayOnSiteL10N format: 2023-01-29
    // jcDetails.startTimeL10N format: 3:00:00 PM
    dueDate = new Date(
      jcDetails.firstDayOnSiteL10N +
        " " +
        formatedStartTime(jcDetails.startTimeL10N)
    );
  }

  useEffect(() => {
    if (totalTasks === data.tasks.length && completedTasks === totalTasks) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  }, [completedTasks, totalTasks, data]);

  return (
    <Col>
      <Col backgroundColor={majorSectionBackgroundColor}>
        <Row
          alignItems="center"
          gridGap="S200"
          padding={"1rem"}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <TriggerButton
            iconAltText={data.name}
            isExpanded={isExpanded}
            onClick={() => setIsExpanded(!isExpanded)}
          />
          <Text color={"primary70"}> {data.name} </Text>
          <Text color={"neutral90"}>
            {completedTasks}/{totalTasks}
          </Text>
        </Row>
        <ExpanderContent isExpanded={isExpanded}>
          {data.tasks.map((task: Task, index: number) => (
            <TaskItem
              key={task.titleText + index}
              task={task}
              applicationId={applicationId}
              candidateId={candidateId}
              bundle={bundle}
              icon={task.icon}
              onTaskStatusEvent={onTaskStatusEvent}
              stage={stage}
              dueDate={dueDate}
              shouldOverrideStatus={task.shouldOverrideStatus}
            />
          ))}
        </ExpanderContent>
      </Col>
    </Col>
  );
};

interface TaskGroupAggregatorProps {
  groups: TaskGroup[];
  applicationId: string;
  candidateId: string;
  stage: Stage;
}
export const TaskGroupAggregator = ({
  groups,
  applicationId,
  candidateId,
  stage,
}: TaskGroupAggregatorProps) => {
  const bundle = useArb("HVHChecklistPantherResource");
  const activitiesText = bundle.getMessage("Checklist-MainPage-SubTitle4");
  const [taskStatusMap, setTaskStateMap] = useState(
    new Map<string, StatusEnum>()
  );
  const [nheDetails, setNheDetails] = useState<NheDetails | undefined>();
  const [jcDetails, setJcDetails] = useState<JobScheduleDetails | undefined>();

  useEffect(() => {
    (async () => {
      const nheDetails = await getNHEAppointment(applicationId);
      if (nheDetails && !nheDetails.errorStatusCode && !nheDetails?.inPast) {
        setNheDetails(nheDetails);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const jcDetails = await getJobScheduleDetails(applicationId);
      if (jcDetails?.error?.errorCode !== "APPLICATION_DETAILS_NOT_FOUND") {
        setJcDetails(jcDetails);
      }
    })();
  }, []);

  const taskStatusHandler = (k?: string, v?: StatusEnum) => {
    if (k && v) {
      setTaskStateMap((prevState) => {
        return new Map(prevState).set(k, v);
      });
    }
  };

  const result = (
    <Col backgroundColor={newUIBackgroundColor}>
      <Row padding="1rem" justifyContent={"space-between"}>
        <H2>{activitiesText}</H2>
        <ProgressBar taskStatusMap={taskStatusMap} bundle={bundle} />
      </Row>
      {groups.map((taskGroup: TaskGroup, index: number) => (
        <TasksGroup
          key={taskGroup.name + index}
          data={taskGroup}
          applicationId={applicationId}
          candidateId={candidateId}
          onTaskStatusEvent={taskStatusHandler}
          taskStatusMap={taskStatusMap}
          stage={stage}
          nheDetails={nheDetails}
          jcDetails={jcDetails}
          bundle={bundle}
        />
      ))}
    </Col>
  );

  return result;
};

export const renderBGColor = (
  status?: StatusEnum,
  shouldOverrideStatus?: Record<string, StatusEnum>
) => {
  const overrideStatus =
    status && shouldOverrideStatus && shouldOverrideStatus[status]
      ? shouldOverrideStatus[status]
      : status;
  switch (overrideStatus) {
    case StatusEnum.COMPLETED:
      return "#008574";
    case StatusEnum.API_ERROR:
      return "#000000";
    case StatusEnum.ERROR:
      return "#BB2929";
    case StatusEnum.INELIGIBLE:
    case StatusEnum.NOT_AVAILABLE_YET:
      return "#56616C";
    default:
      return "#CA8700";
  }
};

function getTaskDetailsButtons(
  taskId?: string,
  taskName?: string,
  status?: StatusEnum,
  shouldOverrideStatus?: Record<string, StatusEnum>
): React.ReactNode {
  if (taskName === ORDER_YOUR_SAFETY_SHOE) {
    if (!taskId || status == StatusEnum.COMPLETED) {
      return null;
    }

    return <ZapposPurchasePromotionButton href={ZAPPOS_URL} taskId={taskId} />;
  } else if (taskName === I_9_DOCUMENT) {
    if (!status) {
      return null;
    }
    return (
      <I9TaskCardButton
        status={status}
        shouldOverrideStatus={shouldOverrideStatus}
      />
    );
  } else if (taskName === BADGE_PHOTO) {
    if (!status) {
      return null;
    }
    return (
      <BadgePhotoTaskCardButton
        status={status}
        shouldOverrideStatus={shouldOverrideStatus}
      />
    );
  } else if (taskName === EMPLOYMENT_PAPERWORK) {
    if (!status || status == StatusEnum.COMPLETED) {
      return null;
    }
    return <EmploymentPaperworkButton />;
  } else if (taskName === CREATE_EMPLOYEE_A_TO_Z_ACCOUNT) {
    if (!status) {
      return null;
    }
    return <CreateEmployeeAccountButton status={status} href={ATOZ_ENDPOINT} />;
  }
}

function getTaskDetailsFlyoutContent(
  taskName: string | undefined,
  taskId?: string,
  status?: StatusEnum,
  shouldOverrideStatus?: Record<string, StatusEnum>
): React.ReactNode {
  if (taskName == undefined) return null;
  switch (taskName) {
    case ORDER_YOUR_SAFETY_SHOE: {
      if (!taskId || !status) {
        return null;
      }
      return <ZapposPurchasePromotion status={status} taskId={taskId} />;
    }
    case I_9_DOCUMENT: {
      if (!status) {
        return null;
      }
      return (
        <I9TaskCard
          status={status}
          shouldOverrideStatus={shouldOverrideStatus}
        />
      );
    }
    case BADGE_PHOTO: {
      if (!status) {
        return null;
      }
      return (
        <BadgePhoto
          status={status}
          shouldOverrideStatus={shouldOverrideStatus}
        />
      );
    }
    case NEW_HIRE_VIDEO: {
      return <NewHireVideoFlyout />;
    }
    case EMPLOYMENT_PAPERWORK: {
      if (!status) {
        return null;
      }
      return <EmploymentPaperwork status={status} />;
    }
    case CREATE_EMPLOYEE_A_TO_Z_ACCOUNT: {
      if (!status) {
        return null;
      }
      return <AtoZAccountCreation status={status} href={ATOZ_ENDPOINT} />;
    }
    default: {
      return <Text>Unknown task</Text>;
    }
  }
}
